import React, { useState, useEffect, useRef } from 'react';
import { Plus, Image as ImageIcon, Trash2, FolderPlus } from 'lucide-react';
import toast from 'react-hot-toast';
import { confirmAction } from '../../utils/confirmToast';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import Drawer from '../../components/common/Drawer/Drawer';
import api from '../../api/axios';
import styles from './Gallery.module.css';

const Gallery = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('All Photos');
  const [imageFile, setImageFile] = useState(null);
  const fileInputRef = useRef(null);

  const defaultAlbums = ['Events', 'Festivals', 'Society', 'Community', 'Kids', 'Sports'];
  const derivedAlbums = Array.from(new Set(photos.map(p => p.album).filter(Boolean)));
  const customAlbums = derivedAlbums.filter(a => !defaultAlbums.includes(a) && a !== 'Other');
  const availableAlbums = [...defaultAlbums, ...customAlbums, 'Other'];
  const tabAlbums = ['All Photos', ...defaultAlbums, ...customAlbums];

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/community/gallery');
      setPhotos(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const [formData, setFormData] = useState({ title: '', album: 'Events', customAlbum: '' });

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const getImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    const normalized = path.replace(/\\/g, '/');
    const baseUrl = import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '');
    if (normalized.startsWith('/uploads')) {
        return `${baseUrl}/api${normalized}`;
    }
    return `${baseUrl}${normalized.startsWith('/') ? '' : '/'}${normalized}`;
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!imageFile) {
      toast.error('Please select an image file to upload.');
      return;
    }

    try {
      const uploadData = new FormData();
      uploadData.append('image', imageFile);

      const uploadRes = await api.post('/upload', uploadData);

      const imageUrl = uploadRes.data; // Save relative URL in backend

      const finalAlbum = formData.album === 'Other' ? formData.customAlbum : formData.album;

      const submitData = {
        ...formData,
        album: finalAlbum,
        url: imageUrl
      };
      
      const { data } = await api.post('/community/gallery', submitData);
      setPhotos([data, ...photos]);
      setIsDrawerOpen(false);
      setFormData({ title: '', album: 'Events', customAlbum: '' });
      setImageFile(null);
      toast.success('Photo added to gallery successfully');
    } catch (err) {
      toast.error('Failed to add photo');
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    confirmAction("Are you sure you want to delete this photo?", async () => {
      try {
        await api.delete(`/community/gallery/${id}`);
        setPhotos(photos.filter(p => p._id !== id));
        toast.success('Photo deleted successfully');
      } catch (err) {
        toast.error('Failed to delete photo');
      }
    });
  };

  const filteredPhotos = activeTab === 'All Photos' 
    ? photos 
    : photos.filter(photo => photo.album === activeTab);

  return (
    <div className={styles.container}>
      <PageHeader 
        title="Society Gallery" 
        breadcrumb={['Resources', 'Gallery']} 
        actionButton={
          <button className={styles.addBtn} onClick={() => setIsDrawerOpen(true)}>
            <Plus size={16} /> Upload Photo
          </button>
        }
      />

      <div className={styles.albumTabs}>
        {tabAlbums.map(tab => (
          <button 
            key={tab} 
            className={activeTab === tab ? styles.activeTab : styles.tab}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className={styles.grid}>
        {loading ? <p style={{padding: '0 24px'}}>Loading gallery...</p> : filteredPhotos.map(photo => (
          <div key={photo._id} className={styles.photoCard}>
            <img src={getImageUrl(photo.url)} alt={photo.title} className={styles.image} />
            <div className={styles.overlay}>
              <div className={styles.photoInfo}>
                <h4>{photo.title}</h4>
                <span>{photo.album}</span>
              </div>
              <button className={styles.btnDelete} title="Delete Photo" aria-label="Delete Photo" onClick={() => handleDelete(photo._id)}>
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <Drawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} title="Upload Photo">
        <form className="drawerForm" onSubmit={handleSubmit}>
          <div className={styles.uploadArea} onClick={() => fileInputRef.current.click()} style={{cursor: 'pointer'}}>
            {imageFile ? (
              <p style={{color: '#10b981', fontWeight: 500}}>Selected: {imageFile.name}</p>
            ) : (
              <>
                <ImageIcon size={32} color="#9ca3af" />
                <p>Click to select image file</p>
                <small>Supports JPG, PNG (Max 5MB)</small>
              </>
            )}
            <input 
              type="file" 
              ref={fileInputRef} 
              style={{display: 'none'}} 
              accept="image/*" 
              onChange={handleFileChange} 
            />
          </div>

          <div className="formGroup">
            <label>Photo Title</label>
            <input name="title" value={formData.title} onChange={handleChange} required placeholder="e.g. Diwali Lighting" />
          </div>
          <div className="formGroup">
            <label>Album / Category</label>
            <select name="album" value={formData.album} onChange={handleChange}>
              {availableAlbums.map(album => (
                <option key={album} value={album}>{album}</option>
              ))}
            </select>
          </div>

          {formData.album === 'Other' && (
            <div className="formGroup">
              <label>Custom Album Name</label>
              <input type="text" name="customAlbum" value={formData.customAlbum} onChange={handleChange} required placeholder="Enter custom album" />
            </div>
          )}
          
          <div className="formActions">
            <button type="button" className="btnCancel" onClick={() => setIsDrawerOpen(false)}>Cancel</button>
            <button type="submit" className="btnSubmit">Upload Photo</button>
          </div>
        </form>
      </Drawer>
    </div>
  );
};

export default Gallery;
